import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { EmailVerification } from '@/components/auth/EmailVerification';
import { PasswordReset } from '@/components/auth/PasswordReset';
import { OnboardingWizard } from '@/components/auth/OnboardingWizard';
import { Eye, EyeOff, UserPlus, LogIn, Mail, KeyRound, CheckCircle } from 'lucide-react';
import { z } from 'zod';

// CRITICAL SECURITY: Input validation schemas
const loginSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }).max(255),
  password: z.string().min(6, { message: 'Contraseña debe tener al menos 6 caracteres' }).max(100)
});

const signupSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }).max(255),
  password: z.string().min(6, { message: 'Contraseña debe tener al menos 6 caracteres' }).max(100),
  confirmPassword: z.string(),
  firstName: z.string().trim().min(2, { message: 'Nombre debe tener al menos 2 caracteres' }).max(50),
  lastName: z.string().trim().min(2, { message: 'Apellido debe tener al menos 2 caracteres' }).max(50)
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword']
});

type AuthMode = 'login' | 'signup' | 'verify' | 'reset' | 'onboarding';

export const EnhancedAuth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signIn, signUp, loading } = useAuth();
  const { toast } = useToast();
  
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // Detect role from URL path
  const detectRoleFromPath = (): 'client' | 'therapist' => {
    const path = window.location.pathname;
    if (path.includes('therapist')) return 'therapist';
    if (path.includes('client')) return 'client';
    return 'client'; // Default
  };
  
  const [userRole, setUserRole] = useState<'client' | 'therapist'>(detectRoleFromPath());
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Handle URL params and detect mode from URL
  useEffect(() => {
    const verified = searchParams.get('verified');
    const reset = searchParams.get('reset');
    const tab = searchParams.get('tab');
    const path = window.location.pathname;
    
    // Detect initial mode from path
    if (path.includes('signup')) {
      setMode('signup');
    } else if (path.includes('forgot') || path.includes('reset')) {
      setMode('reset');
    }
    
    // Handle tab parameter
    if (tab === 'signup') {
      setMode('signup');
    }
    
    if (verified === 'true') {
      toast({
        title: 'Email verificado',
        description: '¡Tu email ha sido verificado exitosamente!',
      });
      setMode('login');
    }
    
    if (reset === 'true') {
      toast({
        title: 'Restablece tu contraseña',
        description: 'Ingresa tu nueva contraseña',
      });
    }
  }, [searchParams, toast]);

  // Redirect if authenticated
  useEffect(() => {
    if (user && mode !== 'onboarding') {
      // Check if user needs onboarding
      checkUserProfile();
    }
  }, [user, mode]);

  const checkUserProfile = async () => {
    if (!user) return;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile?.first_name || !profile?.last_name) {
        setMode('onboarding');
      } else {
        // Get user role from user_roles table
        const { data: userRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        // Redirect to appropriate portal
        if (userRole?.role === 'therapist') {
          navigate('/therapist-portal');
        } else {
          navigate('/client-portal');
        }
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // CRITICAL SECURITY: Validate inputs with Zod
    try {
      const validated = loginSchema.parse({ email, password });
      
      setFormLoading(true);
      const { error } = await signIn(validated.email, validated.password);
      if (!error) {
        // Success handled by auth context and useEffect
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: 'Error de Validación',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      }
      // Other errors handled by auth context
    } finally {
      setFormLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // CRITICAL SECURITY: Validate inputs with Zod
    try {
      const validated = signupSchema.parse({
        email,
        password,
        confirmPassword,
        firstName,
        lastName
      });

      setFormLoading(true);
      const { error } = await signUp(validated.email, validated.password, {
        first_name: validated.firstName,
        last_name: validated.lastName,
        role: userRole
      });
      
      if (!error) {
        setMode('verify');
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: 'Error de Validación',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      }
      // Other errors handled by auth context
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setShowPassword(false);
  };

  if (mode === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <EmailVerification 
          email={email}
          onBack={() => {
            setMode('signup');
            resetForm();
          }}
        />
      </div>
    );
  }

  if (mode === 'reset') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <PasswordReset 
          onBack={() => {
            setMode('login');
            resetForm();
          }}
        />
      </div>
    );
  }

  if (mode === 'onboarding') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <OnboardingWizard 
          userRole={userRole}
          onComplete={() => {
            toast({
              title: 'Onboarding completado',
              description: 'Tu perfil ha sido configurado exitosamente',
            });
            checkUserProfile(); // Will redirect to appropriate portal
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            {mode === 'login' ? <LogIn className="h-6 w-6 text-primary" /> : <UserPlus className="h-6 w-6 text-primary" />}
          </div>
          <CardTitle className="text-2xl">
            {mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
            {userRole === 'therapist' && <Badge className="ml-2" variant="secondary">Terapeuta</Badge>}
            {userRole === 'client' && <Badge className="ml-2" variant="secondary">Cliente</Badge>}
          </CardTitle>
          <CardDescription>
            {mode === 'login' 
              ? `Accede a tu ${userRole === 'therapist' ? 'portal profesional' : 'cuenta'} de Global Therapy Hub`
              : `Únete como ${userRole === 'therapist' ? 'profesional de la salud mental' : 'cliente en búsqueda de bienestar'}`
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={mode} onValueChange={(value) => {
            setMode(value as AuthMode);
            resetForm();
          }}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Iniciar Sesión</TabsTrigger>
              <TabsTrigger value="signup">Registrarse</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu-email@ejemplo.com"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Tu contraseña"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  disabled={formLoading || loading}
                >
                  {formLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
                
                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setMode('reset')}
                  >
                    ¿Olvidaste tu contraseña?
                  </Button>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo de Cuenta</Label>
                  <Select value={userRole} onValueChange={(value: 'client' | 'therapist') => setUserRole(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Cliente - Busco terapia</SelectItem>
                      <SelectItem value="therapist">Terapeuta - Ofrezco servicios</SelectItem>
                    </SelectContent>
                  </Select>
                  {userRole === 'therapist' && (
                    <Badge variant="secondary" className="w-fit">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Sujeto a verificación profesional
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Nombre</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Tu nombre"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Apellido</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Tu apellido"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu-email@ejemplo.com"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite tu contraseña"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  disabled={formLoading || loading}
                >
                  {formLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};